import { useState, useEffect, useMemo } from "react";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useModel } from "@rapid/contexts/ModelContext";
import { SidebarActions } from "@rapid/components/layout/SidebarActions";
import { SidebarHeader } from "@rapid/components/layout/SidebarHeader";
import { PromptLabelList } from "../PromptLabel/PromptLabelList";
import { useFlow } from "@rapid/contexts/FlowContext";
import { DEFAULT_CHILD_ANIMATION_DELAY, DEFAULT_DURATION } from "@rapid/constants/motion";
import { MediaItemStatus } from "@rapid/types/media";
import { GeneratePredictions } from "./GeneratePredictions";
import { BuildModelBtn } from "./BuildModelBtn";
import { BuildPromptSteps } from "@rapid/types/flows";
import { useCurrentMedia, useMediaStore } from "@rapid/store";
import { useIsGeneratingPredictions } from "@rapid/hooks/useIsGeneratingPredictions";
import { getExtractedFrameIdsForMediaItem } from "@rapid/hooks/useMediaUtils";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { useModelActionButtonText } from "@rapid/hooks/useModelActionButtonText";
import { LabelingGuide } from "@rapid/components/tutorial/LabelingGuide";
import Button from "@scripts/react/components/Button";

export const PromptAnnotateDataSidebarContent = () => {
    const { annotationsForFileAndFrameId, approvedFileIds } = useModel();
    const { flowLocked } = useFlow();
    const isGeneratingPredictions = useIsGeneratingPredictions();
    const { pendingMediaItems, mediaItems } = useMedia();
    const media = useMediaStore();
    const currentMedia = useCurrentMedia();
    const { currentFrameAnnotations } = useComputedValues();

    const [showCTAs, setShowCTAs] = useState(false);
    const [showLabelingGuide, setShowLabelingGuide] = useState(false);

    // Show CTAs after parent animation completes
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowCTAs(true);
        }, DEFAULT_CHILD_ANIMATION_DELAY); // Slightly longer than parent animation

        return () => clearTimeout(timer);
    }, []);

    // Check if there are any confirmed (non-draft) annotations
    const hasConfirmedAnnotations = useMemo(() => {
        const allAnnotationsAcrossFiles = Object.values(annotationsForFileAndFrameId).flatMap(
            (annotationsForFile) => Object.values(annotationsForFile).flat()
        );

        // Check if there are any annotations that are not in draft state
        return allAnnotationsAcrossFiles.some((anno) => anno && !anno.draft);
    }, [annotationsForFileAndFrameId]);

    // Check if user has approved predictions (has annotations that came from predictions)
    const hasApprovedPredictions = useMemo(() => {
        // Check if the current file has been approved (user clicked "approve" after generating predictions)
        const currentFileApproved =
            currentMedia?.file.id && approvedFileIds.has(currentMedia?.file.id);

        // If file was approved, also check if current frame has any confirmed annotations
        // If user deleted all boxes, revert to initial state
        if (currentFileApproved) {
            const hasConfirmedAnnotationsOnCurrentFrame = currentFrameAnnotations.some(
                (anno) => !anno.draft
            );
            return hasConfirmedAnnotationsOnCurrentFrame;
        }

        return false;
    }, [currentMedia?.file.id, approvedFileIds, currentFrameAnnotations]);

    // Check if current file has confirmed annotations
    const currentFileHasConfirmedAnnotations = useMemo(() => {
        return currentFrameAnnotations.some((anno) => !anno.draft);
    }, [currentFrameAnnotations]);

    // Determine if this is a new file (has confirmed annotations elsewhere but not on current file)
    const isNewFileWithoutAnnotations = useMemo(() => {
        return hasConfirmedAnnotations && !currentFileHasConfirmedAnnotations;
    }, [hasConfirmedAnnotations, currentFileHasConfirmedAnnotations]);

    // Check if user has uploaded multiple files
    const hasMultipleFiles = useMemo(() => {
        return mediaItems.length > 1;
    }, [mediaItems]);

    // Check if files are still being uploaded/processed
    const hasFilesUploading = useMemo(() => {
        return (
            pendingMediaItems.length > 0 ||
            mediaItems.some((item) => {
                const extractedFrameIds = getExtractedFrameIdsForMediaItem(item);
                const uploadingExtractedFrames = extractedFrameIds.some(
                    (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
                );

                return (
                    item.status === MediaItemStatus.UPLOADING ||
                    item.status === MediaItemStatus.PROCESSING ||
                    uploadingExtractedFrames
                );
            })
        );
    }, [media, pendingMediaItems, mediaItems]);

    const buttonText = useModelActionButtonText(mediaItems);

    const editBoxesDescription = "Add a new prompt";

    return (
        <>
            <SidebarHeader
                title={
                    hasApprovedPredictions
                        ? hasMultipleFiles
                            ? `${editBoxesDescription}, go to the next file, or click "${buttonText}" if everything looks good.`
                            : `${editBoxesDescription} or click "${buttonText}" if everything looks good.`
                        : "What object are you looking for?"
                }
                description={null}
            />
            <div className="content">
                <GeneratePredictions />
                <PromptLabelList mode={BuildPromptSteps.ANNOTATE_DATA} />
            </div>
            <SidebarActions
                toolbarProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: showCTAs ? 1 : 0 },
                    transition: { duration: DEFAULT_DURATION.slower, ease: "easeOut" }
                }}
            >
                <Button type="purbo-ghost" onClick={() => setShowLabelingGuide(!showLabelingGuide)}>
                    <i className="far fa-info-circle"></i>
                    Labeling Tips
                </Button>
                <BuildModelBtn
                    disabled={
                        flowLocked ||
                        hasFilesUploading ||
                        !hasConfirmedAnnotations ||
                        isGeneratingPredictions
                    }
                    loading={hasFilesUploading}
                />
            </SidebarActions>
            <LabelingGuide isOpen={showLabelingGuide} onClose={() => setShowLabelingGuide(false)} />
        </>
    );
};
